import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Product from './components/Product';
import services from '../../services/services';
import utils from '../../utils/utils';
import { AppContext } from '../../context/appContext';
import CategoryFilter from './components/CategoryFilter';
import { PriceContext } from '../../context/priceContext';

const ProductList = ({ addOrder, orderList, showImage }) => {
  const [productList, setProductList] = useState([
    {
      id: '123456789',
      name: 'Kenyér',
      description: 'Nagyon finom és jó',
      unit: 'kg',
      price: '250',
      maxAmount: '5'
    },
    {
      id: '1289',
      name: 'Zsemle',
      description: 'Mint a kenyér, csak kisebb és kerek',
      unit: 'db',
      price: '25',
      maxAmount: '20'
    }
  ]);
  const [hasImages, setHasImages] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [activeFilter, setActiveFilter] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState('');

  const [settings] = useContext(AppContext);
  const [priceData, setPriceData] = useContext(PriceContext);

  const getProductList = async () => {
    const response = await services.getProductList();

    if (response.data.status === 'OK') {
      setProductList(response.data.list);
      setHasImages(response.data.hasImages);
    }
  };

  const getCategoryList = async () => {
    const response = await services.getCategoryList();

    if (response.data.status === 'OK') {
      setCategoryList(response.data.list);
      if (Array.isArray(response.data.list) && response.data.list.length) {
        setActiveFilter(response.data.list[0].id);
      }
    }
  };

  const handleSearchChange = value => {
    setSearch(value);

    filterProducts(value);
  };

  const filterProducts = utils.debounce(value => {
    let filteredList = productList;
    if (activeFilter) {
      filteredList = productList.filter(product =>
        product?.categories?.includes(activeFilter)
      );
    }

    if (value) {
      filteredList = filteredList.filter(product =>
        product?.name.toLowerCase().includes(value.toLowerCase())
      );
    }

    setFilteredProducts(filteredList);
  }, 200);

  useEffect(() => {
    getProductList();
    getCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterProducts();
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList, activeFilter]);

  useEffect(() => {
    const total = () => {
      const priceArray = Object.entries(orderList)?.map(item => {
        const unitAmount = item[1];
        // eslint-disable-next-line
        const singlePrice = productList.filter(el => el.id == item[0])[0].price;

        return unitAmount * singlePrice;
      });

      const result = priceArray.reduce((a, b) => a + b, 0);

      return result > 0 ? Math.round(result) : '';
    };

    setPriceData({ ...priceData, productTotal: total() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderList]);

  const activateFilter = filter => {
    setActiveFilter(filter);
  };

  const getActiveFilterString = () => {
    return categoryList.filter(elem => elem.id === activeFilter)[0].name;
  };

  return (
    <>
      <ProductListHeader bgcolor={settings?.productListHeader || '#e7c37a'}>
        <h3>Mit vásárolnál?</h3>
        <CategoryFilter
          categoryList={categoryList}
          activeFilter={activeFilter}
          activateFilter={activateFilter}
          filterProducts={filterProducts}
          handleChange={handleSearchChange}
          value={search}
        />
      </ProductListHeader>
      <Root bgcolor={settings?.productListBackground || `#ffe0a2`}>
        <div className="formWrapper">
          <Header>
            <Title>
              {activeFilter ? getActiveFilterString() : 'Termékeink'}
            </Title>
            <Title className="right">Mennyit kérsz belőle?</Title>
          </Header>
          {filteredProducts.map(product => (
            <Product
              key={product.id}
              data={product}
              onChange={addOrder}
              value={orderList[product.id]}
              showImage={showImage}
              hasImages={hasImages}
            />
          ))}
          <Total>
            <TotalText>Összesen:</TotalText>
            <TotalText>
              {priceData.productTotal > 0 &&
                `${utils.priceMask(priceData.productTotal)} Ft`}
            </TotalText>
          </Total>
        </div>
      </Root>
    </>
  );
};

export default ProductList;

ProductList.propTypes = {
  addOrder: PropTypes.func.isRequired,
  orderList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showImage: PropTypes.func
};

ProductList.defaultProps = {
  showImage: null
};

const Root = styled.div`
  width: 100%;
  background-color: ${root => root.bgcolor};
  padding: 2rem;

  & .formWrapper {
    max-width: 1200px;
    margin: 50px auto;
  }
`;

const ProductListHeader = styled.div`
  width: 100%;
  background-color: ${props => props.bgcolor};
  padding: 1em;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  & h3 {
    font-size: 2.25em;
    text-align: center;
    width: 100%;

    @media (max-width: 440px) {
      font-size: 1.5em;
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 750px) {
    flex-direction: column;
    width: 100%;
    margin-left: 0;
  }
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  & h4 {
    margin-top: 0;
  }
`;

const Title = styled.h4`
  font-size: 2em;

  @media (max-width: 750px) {
    &.right {
      text-align: right;
      margin-top: 0;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:not(.right) {
      margin-bottom: 0;
    }
  }

  @media (max-width: 550px) {
    font-size: 1.35em;
  }
`;

const TotalText = styled.h4`
  font-size: 2em;
  margin-right: 7.5%;

  @media (max-width: 800px) {
    margin-right: 0;
  }

  @media (max-width: 500px) {
    font-size: 1.5em;
  }
`;
